import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { fetchTickers } from "../lib/api";

/**
 * Brutalist A→Z index of every ticker covered by an approved tool, plus
 * a count column. Sibling of `/sitemap` — gives Googlebot a single flat
 * document listing every `/t/<TICKER>` URL, and gives readers a single
 * place to skim "what does TerminalValue cover for $X".
 *
 * Hidden from non-Google crawlers via the data tag isn't needed —
 * actual `<Link>` elements + canonical meta do the work.
 */
export default function TickerIndex() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    fetchTickers().then((r) => setRows(r || [])).catch(() => {});
  }, []);

  // Two views: by count (default, most-covered first) and A→Z for
  // crawlers + readers who know the ticker they're looking for.
  const byCount = useMemo(
    () =>
      [...rows].sort(
        (a, b) => b.count - a.count || a.ticker.localeCompare(b.ticker),
      ),
    [rows],
  );
  const alpha = useMemo(
    () => [...rows].sort((a, b) => a.ticker.localeCompare(b.ticker)),
    [rows],
  );
  const totalCovered = rows.reduce((s, r) => s + r.count, 0);

  return (
    <div className="bg-white text-black min-h-screen" data-testid="tickers-page">
      <Seo
        title="Tickers covered"
        path="/tickers"
        description={`Index of every stock ticker covered by an approved tool on TerminalValue.io — ${rows.length} tickers across ${totalCovered} tool-listings. Each links to the dedicated tracker page.`}
      />
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1
          className="text-xl font-bold border-b border-black pb-2"
          data-testid="tickers-title"
        >
          &gt;_ tickers covered
        </h1>

        <p className="mt-3 text-[12px] text-[#666]">
          {rows.length === 0 ? (
            "No ticker-dedicated tools indexed yet. Most tools in this index cover broad markets — only tools built around one or a few named tickers show up here."
          ) : (
            <>
              {rows.length} ticker{rows.length === 1 ? "" : "s"} covered by{" "}
              {totalCovered} approved tool{totalCovered === 1 ? "" : "s"}. Each
              entry below links to its dedicated landing page at{" "}
              <span className="font-mono text-[#000]">/t/&lt;TICKER&gt;</span>.
            </>
          )}
        </p>

        {rows.length > 0 && (
          <>
            {/* Most-covered first */}
            <section className="mt-6" data-testid="tickers-by-count">
              <h2 className="text-[13px] font-bold text-[#000] border-b border-[#ddd] pb-1">
                most covered
              </h2>
              <ul className="list-none p-0 m-0 mt-2 text-[13px] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-1">
                {byCount.map((r) => (
                  <li key={r.ticker}>
                    <Link
                      to={`/t/${r.ticker}`}
                      className="tv-link"
                      data-testid={`ticker-count-${r.ticker}`}
                    >
                      ${r.ticker}
                    </Link>{" "}
                    <span className="text-[#888] tabular-nums">{r.count}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* A → Z */}
            <section className="mt-8 mb-12" data-testid="tickers-alpha">
              <h2 className="text-[13px] font-bold text-[#000] border-b border-[#ddd] pb-1">
                A → Z
              </h2>
              <ul className="list-none p-0 m-0 mt-2 text-[13px] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-1">
                {alpha.map((r) => (
                  <li key={r.ticker}>
                    <Link
                      to={`/t/${r.ticker}`}
                      className="tv-link"
                      data-testid={`ticker-alpha-${r.ticker}`}
                    >
                      ${r.ticker}
                    </Link>{" "}
                    <span className="text-[#888] tabular-nums">{r.count}</span>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
